import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Calendar as CalendarIcon } from 'lucide-react';
import { formatDateWord, formatDateInputISO } from '@/lib/dateFormat';

/**
 * Shared date picker input.
 * Renders dates in the app-wide standardized Word format (e.g. "Oct 6 2026")
 * and never relies on the browser's native <input type="date"> locale rendering.
 *
 * @param {string} value - ISO-ish date string (yyyy-MM-dd or full ISO)
 * @param {(next: string) => void} onChange - called with '' or 'yyyy-MM-dd'
 */
const DateField = ({
  value,
  onChange,
  placeholder = 'Select date',
  testId,
  clearTestId,
  className = '',
  disabled = false,
  allowClear = true,
}) => {
  const getPickerDate = (dateString) => {
    if (!dateString) return undefined;
    const parsed = new Date(dateString);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={`justify-start text-left font-normal ${className}`}
          data-testid={testId}
        >
          <CalendarIcon className="mr-2 h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
          <span className="truncate">{value ? formatDateWord(value) : placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={getPickerDate(value)}
          onSelect={(date) => onChange(date ? formatDateInputISO(date) : '')}
          initialFocus
        />
        {allowClear && (
          <div className="border-t p-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-full text-xs"
              onClick={() => onChange('')}
              data-testid={clearTestId}
            >
              Clear date
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default DateField;
